import React from 'react';
import { Progress, Button, Space, Typography } from 'antd';
import { ExperimentOutlined, CheckCircleOutlined, RocketOutlined } from '@ant-design/icons';
import { useAuth } from '../hooks/useAuth';
import { useGetTrialStatusQuery } from '../services/nestjs-base-api';
import { useNavigate } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { Utils } from '../utils/utils';

const { Text } = Typography;

export const FreeTrialBanner: React.FC = () => {
    const { user } = useAuth();
    const navigate = useNavigate();

    const orgId = user?.organisation?.id?.toString();
    // Agency users in the portal have no trial of their own and can't access
    // the org subscription endpoint (403s would poll forever). In client
    // context (agencyContext set) the swapped token CAN read the client's org.
    const isAgencyPortalUser = Utils.userIsAgencyUser(user) && !user?.agencyContext;

    const { data: trialStatus } = useGetTrialStatusQuery({
        jwtToken: user?.jwtToken || '',
        orgId: orgId || '',
    }, {
        skip: !user?.jwtToken || !orgId || isAgencyPortalUser,
        pollingInterval: 60000,
    });

    if (!user?.jwtToken || !trialStatus?.isOnFreeTrial) {
        return null;
    }

    // Not during onboarding. The "collect N responses" quota is meaningless
    // before the user has created a survey, and shown at the top of the setup
    // wizard it reads as a threat before they've done anything — one of the
    // signup drop-off points. It reappears on the dashboard once setup is done.
    if (user?.needsOnboarding) {
        return null;
    }

    const handleSubscribe = () => {
        navigate('/dashboard/organisation?tab=billing&showUpgrade=true');
    };

    if (trialStatus.phase === 'response_collection') {
        const { collected, required, daysRemainingForResponses } = trialStatus.responseRequirement;
        const percent = Math.min(100, Math.round((collected / required) * 100));

        return (
            <div
                style={{
                    // Ink (#0F172A) to match the marketing site's trial section
                    // and the onboarding wizard canvas — one consistent dark
                    // with the amber flame/progress as the accent.
                    background: '#0F172A',
                    padding: '10px 24px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 8,
                }}
            >
                <Space size="middle" style={{ flex: 1 }}>
                    <ExperimentOutlined style={{ color: '#F59E0B', fontSize: 18 }} />
                    <Text strong style={{ color: '#ffffff' }}>
                        <FormattedMessage
                            id="freeTrial.banner.responseCollection"
                            defaultMessage="Professional Trial: collect {remaining} more responses in {days} days to unlock your full {totalDays}-day trial"
                            values={{
                                remaining: Math.max(0, required - collected),
                                days: daysRemainingForResponses,
                                totalDays: trialStatus.totalDays,
                            }}
                        />
                    </Text>
                    <Progress
                        percent={percent}
                        size="small"
                        strokeColor="#F59E0B"
                        trailColor="rgba(255,255,255,0.2)"
                        style={{ width: 150, margin: 0 }}
                        format={() => <span style={{ color: '#ffffff', fontSize: 12 }}>{collected}/{required}</span>}
                    />
                </Space>
            </div>
        );
    }

    if (trialStatus.phase === 'active_trial') {
        return (
            <div
                style={{
                    background: 'linear-gradient(135deg, #135200 0%, #237804 100%)',
                    padding: '10px 24px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 8,
                }}
            >
                <Space size="middle">
                    <CheckCircleOutlined style={{ color: '#b7eb8f', fontSize: 18 }} />
                    <Text strong style={{ color: '#ffffff' }}>
                        <FormattedMessage
                            id="freeTrial.banner.activeTrial"
                            defaultMessage="Professional Trial: {days} days remaining"
                            values={{ days: trialStatus.daysRemaining }}
                        />
                    </Text>
                </Space>
                <Button
                    size="small"
                    icon={<RocketOutlined />}
                    onClick={handleSubscribe}
                    style={{
                        background: '#F59E0B',
                        borderColor: '#F59E0B',
                        color: '#ffffff',
                        fontWeight: 600,
                    }}
                >
                    <FormattedMessage
                        id="freeTrial.banner.subscribeNow"
                        defaultMessage="Subscribe Now"
                    />
                </Button>
            </div>
        );
    }

    return null;
};
